import React, {
  RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useTableReset,
} from 'core/hooks';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import Table from 'core/components/VirtualTable';
import {
  BillingProviderPtanDTO,
} from 'dtos';
import {
  getBillingProviderPtans,
} from 'views/EMR/Settings/transport';
import Section from 'views/EMR/Settings/BillingConfigurations/Section';
import {
  commonTableClasses,
} from 'views/EMR/Settings/BillingConfigurations';
import billingProviderPtanColumns from 'views/EMR/Settings/BillingConfigurations/BillingProviderPtan/columns';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import BillingProviderPtanDialog, {
  BillingProviderPtanDialogProps,
} from 'views/EMR/Settings/BillingConfigurations/BillingProviderPtan/Dialog';
import isNil from 'lodash/isNil';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';

export default function BillingProviderPtan() {
  const { tableInstanceRef } = useTableReset();
  const userInfo = useUserInfo();

  const [
    billingProviderPtanDialogParams,
    setBillingProviderPtanDialogParams,
  ] = useState<BillingProviderPtanDialogProps | null>(null);

  useEffect(() => {
    const refetch = () => {
      tableInstanceRef?.current?.refetch?.();
    };
    const unsubscribeFromBillingProviderPtanChange = settingsEventChannel.on('onBillingProviderPtanChange', refetch);
    // Provider edits change the PTAN summaries, and provider deletes cascade to PTANs.
    const unsubscribeFromBillingProviderChange = settingsEventChannel.on('onBillingProviderChange', refetch);

    return () => {
      unsubscribeFromBillingProviderPtanChange();
      unsubscribeFromBillingProviderChange();
    };
  }, []);

  const columns = useMemo(() => (
    userInfo?.flags?.mayEditOrgConfiguration
      ? billingProviderPtanColumns
      : billingProviderPtanColumns.slice(0, -1)
  ), [userInfo]);

  const onAdd = () => {
    setBillingProviderPtanDialogParams({
      billingProviderPtan: null,
    });
  };

  return (
    <>
      <Section
        headerProps={{
          title: 'Billing Provider PTANs',
          onAdd: userInfo?.flags?.mayEditOrgConfiguration ? onAdd : undefined,
          onAddTooltip: 'Add PTAN',
        }}
      >
        <Table<BillingProviderPtanDTO>
          instanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
          getData={getBillingProviderPtans}
          getRowId={(row) => row?.billingProviderPtanId}
          columns={columns}
          emptySlot="No PTANs Found"
          classes={{
            ...commonTableClasses,
            row: '!py-1 !px-2 !items-start ',
          }}
          dataKey="billing-provider-ptans"
          onRowClick={(table, row) => {
            if (userInfo?.flags?.mayEditOrgConfiguration) {
              setBillingProviderPtanDialogParams({
                billingProviderPtan: row?.original,
              });
            }
          }}
        />
      </Section>
      {!isNil(billingProviderPtanDialogParams) && (
        <BillingProviderPtanDialog
          {...billingProviderPtanDialogParams}
          onClose={() => { setBillingProviderPtanDialogParams(null); }}
        />
      )}
    </>
  );
}
